import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useCookieConsent } from './CookieConsentContext';

type ThemeType = 'light' | 'dark';

interface ThemeContextType {
  theme: ThemeType;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Get cookie preferences
  const { cookiePreferences } = useCookieConsent();
  const canSaveTheme = cookiePreferences.theme;

  // Check if user has a preference stored in localStorage or use light theme as default
  const getInitialTheme = (): ThemeType => {
    // If theme cookies are allowed, check localStorage
    if (canSaveTheme) {
      const savedTheme = localStorage.getItem('theme') as ThemeType;
      if (savedTheme) return savedTheme;
    }
    
    // Always default to light theme
    return 'light';
  };

  const [theme, setTheme] = useState<ThemeType>(getInitialTheme);

  useEffect(() => {
    // Apply theme class to the document body
    document.body.classList.remove('light-theme', 'dark-theme');
    document.body.classList.add(`${theme}-theme`);
    
    // Save theme preference to localStorage only if allowed
    if (canSaveTheme) {
      localStorage.setItem('theme', theme);
    }
  }, [theme, canSaveTheme]);

  const toggleTheme = () => {
    setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 